import { Path, useController, useFormContext } from "react-hook-form";
import { ModalLabel } from "./ModalLabel";
import { BackspaceIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/20/solid";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import SecondaryButton from "./buttons/SecondaryButton";
import { TooltipInfo } from "./TooltipInfo";
import { cn } from "helpers/classNames";
import { XCircleIcon } from "@heroicons/react/24/solid";

export type DropdownItem = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Props<T extends Record<string, any>> = {
  items: DropdownItem[];
  name: Path<T>;
  label: string;
  disabled?: boolean;
  validation?: Record<string, any>;
  tooltip?: string;
  // defaultValue?: PathValue<T, Path<T>>;
  action?: () => void;
  actionText?: string;
  required?: boolean;
  className?: string;
  hideClearButton?: boolean;
  onAddClick?: () => void;
  addButtonText?: string;
  emptyText?: string;
};

export const DropdownFormProvider = <T extends Record<string, any>>({
  items,
  name,
  label,
  disabled = false,
  validation,
  action,
  actionText,
  tooltip,
  required = false,
  className,
  hideClearButton = false,
  onAddClick,
  addButtonText = "Skapa ny",
  emptyText = "Inga alternativ",
}: Props<T>) => {
  if ((action && !actionText) || (!action && actionText)) {
    throw Error("Saknar action eller actiontext");
  }

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<T>();

  // Explicitly watch the field value
  const fieldValue = watch(name);

  const { field } = useController({
    name,
    control,
    rules: validation,
  });

  // console.log("DropdownFormProvider render:", {
  //   name,
  //   fieldValue,
  //   controlValue: field.value,
  // });

  const handleClear = () => {
    field.onChange(null);
    field.onBlur(); // Add this to ensure form state updates
  };
  const errorMessage = errors[name]?.message as string;
  const selectedItem = items.find((item) => item.value === fieldValue);

  return (
    <div className={className}>
      <ModalLabel as="div" className="flex items-center justify-between">
        <span className="flex items-center">
          <span className="mr-1">{label}</span>
          {required && <span>*</span>}
          {!!tooltip && <TooltipInfo id={tooltip} text={tooltip} />}
        </span>
        <div className="w-full flex-1"></div>
        <div className="min-w-0 shrink-0">
          {!!action && (
            <SecondaryButton
              size="mini"
              onClick={(e) => {
                e.stopPropagation();
                action();
              }}
            >
              {actionText}
            </SecondaryButton>
          )}
        </div>
      </ModalLabel>
      <div className="flex items-start gap-2">
        <Listbox
          value={fieldValue}
          onChange={field.onChange}
          disabled={disabled}
          key={fieldValue || "empty"}
        >
          <div className="relative mt-1 w-full">
            <ListboxButton className="relative h-[38px] w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left text-base text-gray-900 focus:border-cyan-600 focus:outline-none focus:ring-cyan-600 disabled:text-gray-500 sm:text-sm">
              <span>{selectedItem?.label ?? " "}</span>
              <span
                className={cn(
                  "absolute inset-y-0 right-0 flex items-center pr-2",
                  !!disabled && "hidden",
                )}
              >
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
              </span>
            </ListboxButton>
            <ListboxOptions className="ring-black absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-opacity-5 focus:outline-none sm:text-sm">
              {items.length === 0 && (
                <div className="cursor-default select-none py-2 pl-4 pr-4 italic text-gray-500">
                  {emptyText}
                </div>
              )}
              {items.map((item) => (
                <ListboxOption
                  key={item.value}
                  value={item.value}
                  className={({ focus }) =>
                    cn(
                      "relative cursor-default select-none py-2 pl-4 pr-4 disabled:text-gray-400",
                      focus ? "bg-cyan-600 text-white" : "text-gray-900",
                      item.disabled && "text-gray-500",
                    )
                  }
                  disabled={item.disabled}
                >
                  {({ selected, focus }) => (
                    <>
                      <span>{item.label}</span>
                      {/* {selected && (
                        <span
                          className={cn(
                            "absolute inset-y-0 right-0 flex items-center pr-3 text-cyan-600",
                            focus && "text-white"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </span>
                      )} */}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        {fieldValue && !disabled && !hideClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className="mt-1 py-2 text-gray-400 hover:text-gray-600"
            aria-label="Rensa"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {onAddClick && (
        <button
          type="button"
          onClick={onAddClick}
          className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-gray-700 transition-colors hover:text-cyan-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
        >
          <PlusIcon className="h-4 w-4" />
          <span>{addButtonText}</span>
        </button>
      )}
      {!!errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
};
